import {Component, Input, OnInit, ViewChild} from "@angular/core";
import {FormGroup} from "@angular/forms";
import {Yii} from "libraries/interfaces/Yii";
import {
    DateFieldSetComponent,
    IDateRangeOptionConfig,
    IDateRangeOptionsConfig
} from "app_sasha/shared/components/date-field-set/date-field-set.component";
import {ConfigFieldBuilderService} from "../config-field-builder.service";

import {FIELD_DESCRIPTION_LABELS, FIELD_LABELS, TEMPLATE_LABELS} from "app_sasha/shared/config/booking-wording";
import {HelperService} from "app_sasha/shared/services/helper.service";
import {BookingService} from 'libraries/service/booking/booking.service';
import {IMultiselectOptions} from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import {IFieldSpecs} from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {BOOKING_ARRAY_FIELDS, BOOKING_DATE_FIELDS, BOOKING_NUMBER_RANGE_FIELDS} from "../config";
import {EnumsResourceService} from "libraries/service/booking/enums/enums-resource.service";
import {
    ApiIssueStatus,
    ApiMidOffice,
    ApiXType
} from "libraries/service/resources/booking/interfaces/ApiSearchParameters";
import {
    BookingStatusService,
    BookingXStatus
} from "libraries/component/booking/booking-status/booking-status.service";
import {RuntimeError} from "libraries/service/error-tracing/runtime-error.model";
import {ErrorTracingService} from "libraries/service/error-tracing/error-tracing.service";
import {BookingSearchParameters} from "libraries/interfaces/IBookingSearchParameters";
import {HostAliasResource, IHostCodesAndAliases} from "libraries/service/resources/host-alias/host-alias.resource";
import {EStayPermissions} from "libraries/enums/e-stay-permissions";
import {IGetEnumsAgency, IGetEnumsAgent} from "libraries/service/booking/enums/IGetEnums";
import {PermissionResourceService} from "libraries/service/resources/permission/permissionResource.service";
import {ESashaPermissions} from "libraries/enums/e-sasha-permissions";
import {of as observableOf} from "rxjs";


interface IFieldConfig {
    [name: string]: IFieldSpecs;
}

@Component({
    selector: 'booking-fieldset',
    template: `
        <collapsible [title]="bookingSearchTitle" [open]="open">
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-right"
                     *permissionIf="permission.SHARED_HOST_SHOW">
                    <multiselect [fieldConfig]="formFields.host"
                                 [options]="hostOptions">
                    </multiselect>
                </div>
                <div class="flex-1">
                    <multiselect [fieldConfig]="formFields.type"
                                 [options]="typeOptions">
                    </multiselect>
                </div>
            </div>

            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <chips [fieldConfig]="formFields.code" [toUpper]="chipsToUpper"></chips>
                </div>
                <div class="flex-1">
                    <multiselect [fieldConfig]="formFields.xType"
                                 [options]="xTypeOptions">
                    </multiselect>
                </div>
            </div>

            <div class="col-to-row-s-tb">
                <div *viewsIf="'fields.booking.xStatus'" viewsPage="search"
                     class="flex-1 sep-top e2e-sort-menu-statutes">
                    <multiselect [fieldConfig]="formFields.xStatus"
                                 [options]="xStatusOptions">
                    </multiselect>
                </div>

                <div *viewsIf="'fields.booking.midOffice'" viewsPage="search" class="flex-1 sep-left sep-top">
                    <multiselect [fieldConfig]="formFields.midOffice"
                                 [options]="midOfficeOptions">
                    </multiselect>
                </div>
            </div>

            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <multiselect [fieldConfig]="formFields.issueStatus"
                                 [options]="issueStatusOptions">
                    </multiselect>
                </div>
                <div class="flex-1">
                    <chips [fieldConfig]="formFields.retentionReason" [toUpper]="chipsToUpper">
                    </chips>
                </div>
            </div>


            <div class="col-to-row-s-tb"
                 *permissionIf="sashaPermissions.SASHA_BOOKING_SEARCH_AGENCY_AND_AGENT_LIST_SHOW">
                <div class="flex-1 sep-top" *viewsIf="'fields.booking.agencies'" viewsPage="search">
                    <multiselect [fieldConfig]="formFields.agency | modifyFieldConfigPipe: agencyListModifications"
                                 [options]="agenciesOptions">

                    </multiselect>
                </div>

                <div class="flex-1 sep-top sep-left" *viewsIf="'fields.booking.agents'" viewsPage="search">
                    <multiselect [fieldConfig]="formFields.agent | modifyFieldConfigPipe: agentListModifications"
                                 [options]="agentsOptions">

                    </multiselect>
                </div>
            </div>

            <div class="col-to-row-s-tb"
                 *permissionIf="sashaPermissions.SASHA_BOOKING_SEARCH_AGENCY_AND_AGENT_EXPERT_SHOW">
                <div class="flex-1 sep-top" *viewsIf="'fields.booking.agencies'" viewsPage="search">
                    <chips [fieldConfig]="formFields.agency" [toUpper]="chipsToUpper"></chips>
                </div>

                <div class="flex-1 sep-top sep-left" *viewsIf="'fields.booking.agents'" viewsPage="search">
                    <chips [fieldConfig]="formFields.agent" [toUpper]="chipsToUpper"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.pendingValue" viewsPage="search">
                    <number [fieldConfig]="formFields.pendingValueMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.pendingValue" viewsPage="search">
                    <number [fieldConfig]="formFields.pendingValueMax"></number>
                </div>
            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.pendingPercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.pendingPercentageMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.pendingPercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.pendingPercentageMax"></number>
                </div>
            </div>

            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.paidValue" viewsPage="search">
                    <number [fieldConfig]="formFields.paidValueMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.paidValue" viewsPage="search">
                    <number [fieldConfig]="formFields.paidValueMax"></number>
                </div>
            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.paidPercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.paidPercentageMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.paidPercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.paidPercentageMax"></number>
                </div>
            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.dueValue" viewsPage="search">
                    <number [fieldConfig]="formFields.dueValueMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.dueValue" viewsPage="search">
                    <number [fieldConfig]="formFields.dueValueMax"></number>
                </div>
            </div>
            <div class="col-to-row-s-tb">
                <div class="flex-1 sep-top" viewsShow="fields.booking.duePercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.duePercentageMin"></number>
                </div>

                <div class="flex-1 sep-top sep-left" viewsShow="fields.booking.duePercentage" viewsPage="search">
                    <number [fieldConfig]="formFields.duePercentageMax"></number>
                </div>
            </div>

            <div class="col-to-row-s-tb">
                <date-field-set #dateBookingFieldSet
                                [form]="form"
                                [optionsConfig]="dateOptions">
                </date-field-set>
            </div>
        </collapsible>
    `
})
export class BookingFieldsetComponent implements OnInit {
    @Input()
    public form: FormGroup;

    @Input()
    public open: boolean = true;

    @Input()
    public chipsToUpper: boolean;

    @ViewChild('dateBookingFieldSet')
    public dateBookingFieldSet: DateFieldSetComponent;

    public readonly bookingSearchTitle: string = Yii.t('wui-sasha-js', 'Dossiers');

    public dateOptions: IDateRangeOptionsConfig;

    // Multiselect
    public xTypeOptions: IMultiselectOptions[];
    public midOfficeOptions: IMultiselectOptions[];
    public issueStatusOptions: IMultiselectOptions[];

    public xStatusOptions: IMultiselectOptions[];
    public hostOptions: IMultiselectOptions[];
    public typeOptions: IMultiselectOptions[];
    public agenciesOptions: IMultiselectOptions[] = [];
    public agentsOptions: IMultiselectOptions[] = [];

    public formFields: IFieldConfig;

    public label: { [key: string]: string } = FIELD_LABELS;

    public agencyListModifications: Partial<IFieldSpecs> = {
        label: FIELD_LABELS.agencyList,
        description: FIELD_DESCRIPTION_LABELS.agencyList
    };

    public agentListModifications: Partial<IFieldSpecs> = {
        label: FIELD_LABELS.agentList,
        description: FIELD_DESCRIPTION_LABELS.agentList
    };

    constructor(
        private readonly configFieldBuilderService: ConfigFieldBuilderService,
        private readonly helperService: HelperService,
        private readonly enumsResourceService: EnumsResourceService,
        private readonly bookingService: BookingService,
        private readonly bookingStatusService: BookingStatusService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly hostAliasResource: HostAliasResource,
        private readonly permissionResourceService: PermissionResourceService
    ) {
    }

    public ngOnInit(): void {
        // get types 'Air, Stay, VehicleRental' to fill "contenu" options array
        this.enumsResourceService.listTypes()
            .subscribe((typeList: string[]): void => {
                    this.typeOptions = typeList.map((type: string): IMultiselectOptions => {
                        return {
                            value: type,
                            label: this.bookingService.getTypeLabel(type),
                        };
                    });
                },
                (error: Error): void => {
                    this.errorTracingService.captureException(new RuntimeError("Unable to retrieve the list of available hosts", error));
                    this.hostOptions = [];
                });

        // get connectors to fill host options array
        this.enumsResourceService.listSystems()
            .subscribe((hostCodesAndAliases: IHostCodesAndAliases): void => {
                    this.hostOptions = this.helperService.getMultiselectOptions(hostCodesAndAliases.aliases, hostCodesAndAliases.codes, true);
                },
                (error: Error): void => {
                    this.errorTracingService.captureException(new RuntimeError("Unable to retrieve the list of available hosts", error));
                    this.hostOptions = [];
                });

        this.enumsResourceService.listAgencies()
            .subscribe((agenciesCodeAndName: IGetEnumsAgency[]): void => {
                this.agenciesOptions = agenciesCodeAndName.map((agencies: IGetEnumsAgency): IMultiselectOptions => {
                    return {
                        value: agencies.code,
                        label: agencies.name,
                    };
                });
            });

        this.enumsResourceService.listAgents()
            .subscribe((agentsCodeAndName: IGetEnumsAgent[]): void => {
                this.agentsOptions = agentsCodeAndName.map((agents: IGetEnumsAgent): IMultiselectOptions => {
                    return {
                        value: agents.code,
                        label: agents.name,
                    };
                });
            });

        this.formFields = {
            ...this.configFieldBuilderService.buildFieldConfigArray(this.form, BOOKING_ARRAY_FIELDS),
            ...this.configFieldBuilderService.buildFieldConfigDate(this.form, BOOKING_DATE_FIELDS),
            ...this.configFieldBuilderService.buildFieldConfigNumber(this.form, BOOKING_NUMBER_RANGE_FIELDS),
        };

        this.dateOptions = {
            label: TEMPLATE_LABELS.dateField,
            optionConfig: BOOKING_DATE_FIELDS
                .map((fieldName: BookingSearchParameters): IDateRangeOptionConfig => {
                    return this.configFieldBuilderService.createDateRangeOptionConfig(fieldName, this.formFields);
                }),
        };

        this.xStatusOptions = this.helperService.getMultiselectOptions(
            this.bookingStatusService.statusesLabels,
            Object.values(BookingXStatus),
            true
        );

        this.midOfficeOptions = this.helperService.getMultiselectOptions(
            this.bookingService.midOfficeLabels,
            Object.values(ApiMidOffice),
            true
        );

        this.xTypeOptions = this.helperService.getMultiselectOptions(
            this.bookingService.xTypeLabels,
            Object.values(ApiXType),
            true
        );

        this.issueStatusOptions = this.helperService.getMultiselectOptions(
            this.bookingService.issueStatusLabels,
            Object.values(ApiIssueStatus),
            false
        );
    }

    public permission: typeof EStayPermissions = EStayPermissions;
    public sashaPermissions: typeof ESashaPermissions = ESashaPermissions;
}
